import { useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowUpRight, ArrowLeft, ArrowRight, Lock } from 'lucide-react';
import { PORTFOLIO } from '@/constants/testIds';
import { CaseStudyThumb } from './CaseStudyThumb';
import { useCaseStudyVisibility } from '@/hooks/useCaseStudyVisibility';
import { useAutoAdvance } from '@/lib/useAutoAdvance';

// All 7 real projects from himanshutanty.com/portfolio/
export const projects = [
    {
        slug: 'beyond-the-dashboard',
        no: '09',
        title: 'The Future of Intelligent Trucks',
        category: 'Concept · HMI',
        image:
            'https://static.prod-images.emergentagent.com/jobs/1dbb8773-a62b-4a3e-b60f-4feacdf5c6e3/images/aee3e3f06a641eff411acc39ea06e463fdf9ecfcf510cb553e3dea06e6982610.jpeg',
    },
    {
        slug: 'homepage-redesign',
        no: '01',
        title: 'Engagedly Homepage Redesign',
        category: 'UX Design',
        image:
            'https://framerusercontent.com/images/wv3HYHKNQzH1y5keUPJVlvQgt0.png',
    },
    {
        slug: 'my-communication',
        no: '02',
        title: 'My Communications',
        category: 'UX Design',
        image: '/api/files/95dfc24b-81c2-4073-b135-4b63cb604bd8',
    },
    {
        slug: 'cloudhub-enterprise-redesign',
        no: '03',
        title: 'CloudHub Enterprise Redesign',
        category: 'Enterprise SaaS',
        image: '/api/files/150768d7-29f4-44c7-b1f7-0eb28d06975d',
    },
    {
        slug: 'symptom-tracking',
        no: '04',
        title: 'Symptom Tracking',
        category: 'Mobile App',
        image:
            'https://himanshutanty.com/wp-content/uploads/2025/02/Cover-3-330x260.jpg',
    },
    {
        slug: 'travel-app',
        no: '05',
        title: 'Travel App For Visitors',
        category: 'Mobile App',
        image:
            'https://himanshutanty.com/wp-content/uploads/2024/10/port-4-330x260.avif',
    },
    {
        slug: 'graphic-designs',
        no: '06',
        title: 'Graphic Designs',
        category: 'Graphic Design',
        image:
            'https://himanshutanty.com/wp-content/uploads/2025/02/image-3-2-330x260.png',
    },
    {
        slug: 'portal-for-site-contractors',
        no: '07',
        title: 'Portal For Site Contractors',
        category: 'UX Design',
        image:
            'https://himanshutanty.com/wp-content/uploads/2024/10/port-5-330x260.avif',
    },
    {
        slug: 'web-template',
        no: '08',
        title: 'Individual Development Plan',
        category: 'UX Design',
        image:
            'https://himanshutanty.com/wp-content/uploads/2024/10/port-3-330x260.avif',
    },
];

const VISIBLE = 3;

const Card = ({ p }) => {
    return (
        <div className="shrink-0 basis-full sm:basis-1/2 lg:basis-1/3 pr-3 md:pr-4">
            <Link
                to={`/work/${p.slug}`}
                data-testid={PORTFOLIO.projectCard(p.slug)}
                data-cursor="hover"
                className="group block"
            >
                {/* Thumbnail — custom typographic + geometric composition per slug */}
                <div className="relative overflow-hidden rounded-xl border border-[var(--line)] hover:border-[var(--line-2)] transition-colors">
                    <div className="overflow-hidden aspect-[16/11] relative">
                        <div className="absolute inset-0 group-hover:scale-[1.04] transition-transform duration-[900ms] ease-out">
                            <CaseStudyThumb slug={p.slug} showMeta={false} className="w-full h-full" />
                        </div>
                    </div>
                    <div className="absolute top-2.5 right-2.5 z-10 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-black/55 backdrop-blur-md border border-white/15 font-mono-brand text-[9px] uppercase tracking-[0.14em] text-white">
                        <Lock size={8} className="text-white/80" />
                        {p.category}
                    </div>
                </div>

                {/* Title row — subtle */}
                <div className="mt-2.5 flex items-center justify-between gap-2">
                    <h3 className="font-display-italic text-[15px] md:text-base leading-tight text-[var(--ink)] group-hover:text-[var(--accent)] transition-colors truncate">
                        {p.title}
                    </h3>
                    <ArrowUpRight
                        size={13}
                        className="text-[var(--ink-3)] group-hover:text-[var(--accent)] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all duration-300 shrink-0"
                    />
                </div>
            </Link>
        </div>
    );
};

const Projects = () => {
    const [index, setIndex] = useState(0);
    // Filter out any case study an admin has toggled off in /admin. Defaults
    // to showing everything until the first fetch settles.
    const { isVisible } = useCaseStudyVisibility();
    const list = useMemo(
        () => projects.filter((p) => isVisible(p.slug)),
        [isVisible]
    );
    const maxIndex = Math.max(0, list.length - VISIBLE);
    // If the admin hides enough projects to shrink the carousel below the
    // current viewport, snap the index back into range.
    const boundedIndex = Math.min(index, maxIndex);
    const prev = () => setIndex(() => Math.max(0, boundedIndex - 1));
    const next = () => setIndex(() => Math.min(maxIndex, boundedIndex + 1));

    // Auto-advance the carousel. Wraps back to 0 after reaching the end so
    // recruiters see every project without touching the arrows. Pauses on
    // hover and respects `prefers-reduced-motion`.
    const hoverRef = useRef(null);
    useAutoAdvance({
        enabled: maxIndex > 0,
        interval: 5500,
        onTick: () => setIndex((i) => (i >= maxIndex ? 0 : i + 1)),
        hoverRef,
    });

    return (
        <section
            id="work"
            ref={hoverRef}
            data-testid={PORTFOLIO.projectsSection}
            className="relative px-4 md:px-6 py-10 md:py-12"
        >
            <div className="mx-auto max-w-[1400px]">
                {/* Section head */}
                <div className="flex items-end justify-between gap-6 flex-wrap mb-6 md:mb-8">
                    <div>
                        <span className="chip mb-3 inline-flex">
                            <span className="w-1.5 h-1.5 rounded-full bg-[var(--accent)]" />
                            Selected work
                        </span>
                        <h2 className="font-display leading-[0.95] text-4xl md:text-6xl text-[var(--ink)]">
                            Selected
                            <span className="font-display-italic text-[var(--accent)]"> work</span>.
                        </h2>
                    </div>

                    {/* Compact controls */}
                    <div className="flex items-center gap-4">
                        <div className="flex items-center gap-1.5">
                            <button
                                type="button"
                                onClick={prev}
                                disabled={boundedIndex === 0}
                                data-testid="projects-prev"
                                data-cursor="hover"
                                aria-label="Previous"
                                className="inline-flex items-center justify-center w-9 h-9 rounded-full border border-[var(--line-2)] text-[var(--ink)] hover:bg-[var(--surface)] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                            >
                                <ArrowLeft size={14} />
                            </button>
                            <button
                                type="button"
                                onClick={next}
                                disabled={boundedIndex >= maxIndex}
                                data-testid="projects-next"
                                data-cursor="hover"
                                aria-label="Next"
                                className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-[var(--accent)] text-[var(--accent-ink)] hover:opacity-90 disabled:opacity-30 disabled:cursor-not-allowed transition-opacity"
                            >
                                <ArrowRight size={14} />
                            </button>
                        </div>
                    </div>
                </div>

                {/* Carousel viewport — shows 3 cards at a time */}
                <div className="overflow-hidden -mr-3 md:-mr-4">
                    <motion.div
                        animate={{ x: `-${(100 / VISIBLE) * boundedIndex}%` }}
                        transition={{ duration: 0.65, ease: [0.2, 0.8, 0.2, 1] }}
                        className="flex"
                    >
                        {list.map((p) => (
                            <Card p={p} key={p.slug} />
                        ))}
                    </motion.div>
                </div>

                {/* Progress rail */}
                <div className="mt-8 flex items-center gap-3">
                    <div className="flex-1 h-[1px] bg-[var(--line-2)] relative overflow-hidden">
                        <motion.div
                            animate={{
                                width: `${((boundedIndex + 1) / (maxIndex + 1)) * 100}%`,
                            }}
                            transition={{ duration: 0.5 }}
                            className="absolute inset-y-0 left-0 bg-[var(--accent)]"
                        />
                    </div>
                    <Link
                        to="/work"
                        data-testid={PORTFOLIO.projectsViewAll}
                        data-cursor="hover"
                        className="link-underline font-mono-brand text-[10.5px] uppercase tracking-[0.22em] text-[var(--ink)] whitespace-nowrap"
                    >
                        View All Projects ↗
                    </Link>
                </div>
            </div>
        </section>
    );
};

export default Projects;
